/**
 * Wire seams for the entity-switch service (DV-3011). Plain async functions
 * that dispatch the org-selection RTK Query endpoints on the app store —
 * not hooks — so `switchEntity()` stays callable outside React (Phase B
 * calls it from `bootstrapAuth`, before anything renders). Same pattern as
 * `assetEditSeams.ts`.
 *
 * The store is reached through `getStore()` rather than imported: a module-scope
 * import of `store.ts` here closes a real load cycle (DV-3013 §3.8).
 */
import { getStore } from '../store/get-store';
import { orgSelectionApi } from '../components/organization-selection/services/orgSelectionApi';
import { orderApi } from '../components/orders/services/orderApi';
import type {
  AccessibleOrg,
  EntityCheckoutInfo,
} from '../types/org-selection.types';
import type { EntityHierarchy } from '../types/order.types';

// GET /entity-service/api/v1/entities?entityId=... — the target org's doc,
// with no X-Entity-UUID (`getEntityInfoForSwitch`, DV-3421 review round 6).
// forceRefetch: a cached doc must not satisfy a switch (the DV-2757
// stale-lazy-trigger lesson); a switch needs the live answer.
export async function fetchEntityInfoRequest(
  entityId: string,
): Promise<EntityCheckoutInfo[]> {
  const sub = getStore().dispatch(
    orgSelectionApi.endpoints.getEntityInfoForSwitch.initiate(entityId, {
      forceRefetch: true,
    }),
  );
  try {
    return await sub.unwrap();
  } finally {
    // Queries hold a cache subscription (not a mutation result) — release it.
    sub.unsubscribe();
  }
}

// GET /entity-service/api/v1/entities/scope/check — would the guard HONOUR a
// selection of `entityId` under the caller's CURRENT token? The question a
// switch asks to decide whether the token has to move first, and whether a move
// took. Answered by the backend from the guard's own config: an unrestricted
// caller (whatever `guard.unrestricted-roles` says) gets yes for any existing
// account, with no copy of that list here (DV-3421 review round 4, finding 5);
// and with the guard off or `honour-selection` off, only the token's own entity
// is yes, because no other selection would be obeyed.
//
// NO fallback (review of 291019f0, item 5). The old one asked `/entities/scope`
// — the caller's own subtree — which says no to every customer account for an
// unrestricted caller and knows nothing of `honour-selection`, so it guessed
// wrong exactly where it mattered. entity-service ships the endpoint first, in
// every environment; until it has, a deployed environment answers a missing
// route with a 200 and an empty body (shadowed by /entities/{entityNameOrId})
// or an empty 403 — so anything but a boolean `reachable` is a failure, and
// the switch stops at 'scope-check' rather than guess (review round 2, issue 5).
//
// forceRefetch: a switch must not be decided on a cached answer — the token may
// have moved since.
export async function fetchScopeCheckRequest(entityId: string): Promise<boolean> {
  const sub = getStore().dispatch(
    orgSelectionApi.endpoints.checkScope.initiate(entityId, { forceRefetch: true }),
  );
  let body: { reachable?: unknown } | null | undefined;
  try {
    // Typed as `{ reachable: boolean }`, but read as untrusted: see above.
    body = (await sub.unwrap()) as { reachable?: unknown } | null | undefined;
  } finally {
    sub.unsubscribe();
  }
  const reachable = body?.reachable;
  if (typeof reachable !== 'boolean') {
    throw new Error('GET /entities/scope/check answered without a boolean `reachable`');
  }
  return reachable;
}

// POST /api/v1/users/context — re-anchor the caller's Keycloak context.
//
// DV-3421: restored for CROSS-TREE switches only. DV-3573 removed it from
// every switch because the token stopped needing to move — but that holds
// only inside one tree. The guard derives the allowed set from the token's
// root subtree, so a selection in a DIFFERENT tree is outside it no matter
// what, and is refused on every request until the token itself moves.
//
// A mutation, so there is no cache subscription to release.
export async function setUserContextRequest(
  groupName: string,
  userId: string,
  entityId: string,
): Promise<void> {
  await getStore()
    .dispatch(
      orgSelectionApi.endpoints.setUserContext.initiate({
        groupName,
        userId,
        entityId,
      }),
    )
    .unwrap();
}

// GET /entity-service/api/v1/entities/picker — every internal account.
// forceRefetch: this only runs on a cache MISS during resolution, so a
// cached list is what already failed to answer.
export async function fetchInternalOrgPickerRequest(
  callerRoleSuffix: string,
): Promise<AccessibleOrg[]> {
  const sub = getStore().dispatch(
    orgSelectionApi.endpoints.getInternalOrgPicker.initiate(callerRoleSuffix, {
      forceRefetch: true,
    }),
  );
  try {
    return await sub.unwrap();
  } finally {
    // Queries hold a cache subscription (not a mutation result) — release it.
    sub.unsubscribe();
  }
}

// GET /api/v1/entities/{userId} — the caller's own accessible orgs.
export async function fetchCustomerOrgListRequest(
  userId: string,
): Promise<AccessibleOrg[]> {
  const sub = getStore().dispatch(
    orgSelectionApi.endpoints.getCustomerOrgList.initiate(userId, {
      forceRefetch: true,
    }),
  );
  try {
    return await sub.unwrap();
  } finally {
    // Queries hold a cache subscription (not a mutation result) — release it.
    sub.unsubscribe();
  }
}

// GET /entity-service/api/v1/entities/{uuid} — one entity document.
// Reuses orderApi's getEntityTree endpoint: same URL, same response type
// (EntityHierarchy) as the entity-tree consumers already declared, so a
// second declaration isn't kept in step with this one by hand.
export async function fetchEntityDocRequest(
  entityId: string,
): Promise<EntityHierarchy> {
  const sub = getStore().dispatch(
    orderApi.endpoints.getEntityTree.initiate(entityId, { forceRefetch: true }),
  );
  try {
    return await sub.unwrap();
  } finally {
    // Queries hold a cache subscription (not a mutation result) — release it.
    sub.unsubscribe();
  }
}
